@include host-component-properties;

:host {
  --paf-button-border-weight: var(--paf-global-border-width-2x);
  --paf-button-border-radius: 3;
  --paf-button-transition-duration: 180ms;
  --paf-button-opacity-disabled: 0.65;
  --paf-button-font-size: 16;
  --paf-button-padding-block: 10;
  --paf-button-padding-inline: 18;

  & ::slotted(svg) {
    fill: currentcolor;

    @include forced-colors {
      fill: ButtonText;
    }
  }

  & .secondary ::slotted(svg) {
    @include forced-colors {
      fill: currentcolor;
    }
  }
}

// Variants
// --------
.primary {
  --paf-button-background-color: var(--paf-global-post-brand-dark);
  --paf-button-background-color-hover: var(--paf-global-post-brand-black);
  --paf-button-border-color: var(--paf-global-post-brand-dark);
  --paf-button-border-color-hover: var(--paf-global-post-brand-black);
  --paf-button-color: var(--paf-global-post-brand-white);
  --paf-button-color-hover: var(--paf-global-post-brand-white);
}

.secondary {
  --paf-button-background-color: var(--paf-global-post-brand-white);
  --paf-button-background-color-hover: var(--paf-global-post-brand-white);
  --paf-button-border-color: hsl(0deg 0% 40% / 50%);
  --paf-button-border-color-hover: var(--paf-global-post-brand-black);
  --paf-button-color: var(--paf-global-post-brand-secondary);
  --paf-button-color-hover: var(--paf-global-post-brand-black);
}

.expandable {
  --paf-button-expandable-icon-dimensions: 16;
  --paf-button-expandable-icon-gap: 12;
  --paf-button-expandable-color: var(--paf-global-post-brand-dark);
  --paf-button-padding-block: 12;
  --paf-button-padding-inline: 4;
}

// Icon
// -------
.icon-only {
  --paf-icon-size: 36;
  --paf-icon-size-small: 24;
  --paf-icon-padding-block: 16;
  --paf-icon-padding-inline: 22;
}

.button {
  @include button-reset;

  display: inline-block;
  max-width: 100%;
  width: 100%;
  font-size: px-to-rem(var(--paf-button-font-size));
  text-decoration: none;
  border-radius: px-to-rem(var(--paf-button-border-radius));
  border: px-to-rem(var(--paf-button-border-weight)) solid var(--paf-button-border-color);
  background-color: var(--paf-button-background-color);
  color: var(--paf-button-color);
  transition-duration: var(--paf-button-transition-duration);
  transition-property: border-color, background-color, color;
  padding-block: px-to-rem(var(--paf-button-padding-block));
  padding-inline: px-to-rem(var(--paf-button-padding-inline));
  text-align: center;

  @include forced-colors {
    color: ButtonText;
    background-color: ButtonFace;
    border-color: ButtonBorder;
    transition: none;
  }

  @include reduced-motion {
    transition: none;
  }

  @include mq($from: small) {
    width: auto;
  }

  &:not([disabled]) {
    @include hover-style {
      cursor: pointer;
      border-color: var(--paf-button-border-color-hover);
      background-color: var(--paf-button-background-color-hover);
      color: var(--paf-button-color-hover);

      @include forced-colors {
        color: LinkText;
        background-color: Canvas;
      }
    }
  }

  &[disabled] {
    opacity: var(--paf-button-opacity-disabled);
  }

  & .text {
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;

    & .hidden-text {
      @include screen-reader-only;
    }
  }

  &.secondary {
    @include forced-colors {
      background-color: Canvas;

      &:not([disabled]) {
        @include hover-style {
          color: LinkText;
          border-color: LinkText;
          background-color: Canvas;
        }
      }
    }
  }

  .expand-icon {
    flex-basis: px-to-rem(var(--paf-button-expandable-icon-dimensions));
    flex-shrink: 0;
    height: px-to-rem(var(--paf-button-expandable-icon-dimensions));
    fill: currentcolor;
    transform-origin: 50% 50%;
    transform: rotate(180deg);
    transition: transform var(--paf-button-transition-duration);

    @include reduced-motion {
      transition: none;
    }

    @include forced-colors {
      fill: ButtonText;
    }
  }

  &.expandable {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    column-gap: px-to-rem(var(--paf-button-expandable-icon-gap));
    color: var(--paf-button-expandable-color);

    &.expanded .expand-icon {
      transform: rotate(0deg);
    }
  }

  .icon {
    display: block;
    width: px-to-rem(var(--paf-icon-size-small));
    height: px-to-rem(var(--paf-icon-size-small));

    @include mq($from: medium) {
      width: px-to-rem(var(--paf-icon-size));
      height: px-to-rem(var(--paf-icon-size));
    }
  }

  &.icon-only {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &.rotate-icon {
      .icon {
        transform: rotate3d(0, 0, 1, 90deg);
      }
    }
  }
}
